/* eslint-disable import/no-cycle */
import * as moment from 'moment';
import i18n from '../../../../../i18n/i18n';
import WidgetSettingFormField from '../../../../shared/widgets/WidgetSettingFormField';
import {ValidationRules} from '../../../../../utils/validationRules';
import {CockpitWidgetConfig} from './cockpitWidgetConfig';
import {Operation} from '../../../../../models/operation';
import {Order} from '../../../../../models/order';
import {Material} from '../../../../../models/material';
import {WorkplaceState} from '../../../../../models/workplaceState';
import {PersonnelLog} from '../../../../../models/personnelLog';
import {BaseModel as Hierarchy} from '../../../../../models/base';
import {OperationState} from '../../../../../models/operationState';

export class DataTableWidgetConfig extends CockpitWidgetConfig {
  static SOURCE_HIERARCHY = 'hierarchy';
  static SOURCE_SELECTION = 'selection';
  static SOURCE_GROUP = 'group';

  static LINK_WORKPLACE_NOWHERE = 'none';
  static LINK_WORKPLACE_TO_COCKPIT = 'cockpit';
  static LINK_WORKPLACE_TO_PRODUCTION = 'production';

  static widgetType = {
    identifier: 'data-table',
    name: i18n.t('cockpitWidget.widgetTypeIdentifier.dataTable'),
  };

  static onLoadConfig = (rootStore) => {
    rootStore.sensorStore.loadAll();
  };

  getIsWorkplaceCockpit() {
    return this.rootStore.cockpitStore.currentWorkplaceId;
  }

  loadStateHistoriesByWorkplace(workplaceId) {
    return this.rootStore.stateHistoryStore.loadStateHistoriesByWorkplace({
      workplaceId,
      from: (new Date()).toISOString(),
      to: (new Date()).toISOString(),
    }, true);
  }

  loadActiveOperationByWorkplace(workplaceId) {
    return this.rootStore.operationStore.loadActiveOperationByWorkplace(workplaceId).then((operation) => {
      if (operation && operation.orderId) {
        this.rootStore.orderStore.load(operation.orderId).then((order) => {
          if (order && order.parentOrderId) {
            this.rootStore.orderStore.load(order.parentOrderId);
          }
        });
      }
    });
  }

  loadWorkplaceStateLogs(workplaceId) {
    const fromDate = moment().subtract(1, 'day');
    const toDate = moment();
    return this.rootStore.workplaceStateLogStore.loadManyByWorkplace(workplaceId, {fromDate, toDate});
  }

  loadInspectionTasks(workplaceId) {
    return this.rootStore.inspectionTaskPendingStore.loadByWorkplace(workplaceId);
  }

  loadPersonnelLogs(workplaceId) {
    return this.rootStore.personnelLogStore.loadAll({params: {workplaceId}});
  }

  modelOptions = [
    {
      key: 'operation',
      label: i18n.t('operation.model.one'),
      properties: new Operation().displayableProperties,
      getRecord: ({workplaceId}) => this.rootStore.operationStore.getActiveByWorkplaceId(workplaceId),
      loadAction: 'loadStateHistoriesByWorkplace',
    },
    {
      key: 'operationState',
      label: i18n.t('operationState.model.one'),
      properties: new OperationState().displayableProperties,
      getRecord: ({workplaceId}) => this.rootStore.operationStore.getActiveByWorkplaceId(workplaceId)?.state,
      loadAction: 'loadStateHistoriesByWorkplace',
    },
    {
      key: 'operationMaterial',
      label: i18n.t('material.model.activeOperation'),
      properties: new Material().displayableProperties,
      getRecord: ({workplaceId}) => this.rootStore.operationStore.getActiveByWorkplaceId(workplaceId)?.material,
      loadAction: 'loadStateHistoriesByWorkplace',
    },
    {
      key: 'order',
      label: i18n.t('order.model.one'),
      properties: new Order().displayableProperties,
      getRecord: ({workplaceId}) => this.rootStore.operationStore.getActiveByWorkplaceId(workplaceId)?.order,
      loadAction: 'loadActiveOperationByWorkplace',
    },
    {
      key: 'orderMaterial',
      label: i18n.t('material.model.activeOrder'),
      properties: new Material().displayableProperties,
      getRecord: ({workplaceId}) => this.rootStore.operationStore.getActiveByWorkplaceId(workplaceId)?.order?.material,
      loadAction: 'loadStateHistoriesByWorkplace',
    },
    {
      key: 'workplaceState',
      label: i18n.t('workplaceState.model.one'),
      properties: new WorkplaceState().displayableProperties,
      getRecord: ({workplaceId}) => (
        this.rootStore.workplaceStateLogStore.getLatestOfWorkplace(workplaceId)?.state
      ),
      loadAction: 'loadWorkplaceStateLogs',
    },
    {
      key: 'inspectionTaskPending',
      label: i18n.t('inspectionTaskPending.model.one'),
      properties: [
        {
          key: 'amount',
          title: i18n.t('inspectionTaskPending.amount'),
        },
        {
          key: 'overdueAmount',
          title: i18n.t('inspectionTaskPending.overdueAmount'),
        },
      ],
      getRecord: ({workplaceId}) => {
        const pendingInspectionTasks = this.rootStore.inspectionTaskPendingStore.getByWorkplaceId(workplaceId);
        return ({
          amount: pendingInspectionTasks.length,
          overdueAmount: pendingInspectionTasks.filter((task) => moment(task.dueDate).diff(moment()) < 0).length,
          displayableProperties: [
            {
              key: 'amount',
              render: (text, record) => ({
                props: {},
                children: record.amount,
              }),
            },
            {
              key: 'overdueAmount',
              render: (text, record) => ({
                props: {},
                children: record.overdueAmount,
              }),
            },
          ],
        });
      },
      loadAction: 'loadInspectionTasks',
    },
    {
      key: 'activePersonnel',
      label: i18n.t('personnelLog.model.one'),
      properties: new PersonnelLog().displayableProperties,
      getRecord: ({workplaceId}) => this.rootStore.personnelLogStore.getLatestByWorkplaceId(workplaceId),
      loadAction: 'loadPersonnelLogs',
    },
    {
      key: 'sensor',
      label: i18n.t('sensor.model.one'),
      properties: () => this.rootStore.sensorStore.sensors
        .map((sensor) => ({key: sensor.name, title: sensor.label || sensor.name})),
      getRecord: ({workplaceId}) => ({
        displayableProperties: this.rootStore.sensorStore.sensors.map((sensor) => ({
          key: sensor.name,
          render: () => {
            const workplaceHierarchyId = this.rootStore.workplaceStore.getById(workplaceId).hierarchyId;
            let workplaceHierarchy = !sensor.hierarchyId;
            if (sensor.hierarchyId) {
              const sensorHierarchy = Hierarchy.findInTree(
                this.rootStore.hierarchyStore.hierarchyTree,
                sensor.hierarchyId
              );
              workplaceHierarchy = Hierarchy.findInTree([sensorHierarchy], workplaceHierarchyId);
            }
            return ({
              props: {},
              children: workplaceHierarchy && JSON.stringify(sensor.online),
            });
          },
        })),
      }),
    },
  ];

  get tabs() {
    return [
      this.generalSettingsTab,
      {
        key: 'settings',
        label: i18n.t('dataTableWidgetConfig.settings.title'),
      },
      {
        key: 'dataSource',
        label: i18n.t('dataTableWidgetConfig.dataSource.title'),
      },
    ];
  }

  getAdditionalSettings() {
    if (this.getIsWorkplaceCockpit()) {
      return [
        {
          id: 'workplacesSource',
          label: i18n.t('dataTableWidgetConfig.settings.workplacesSource.label'),
          helpText: i18n.t('dataTableWidgetConfig.settings.workplacesSource.help'),
          type: WidgetSettingFormField.fieldTypes.Select,
          validations: [ValidationRules.required],
          defaultValue: DataTableWidgetConfig.SOURCE_SELECTION,
          options: [
            {
              value: DataTableWidgetConfig.SOURCE_HIERARCHY,
              label: i18n.t('dataTableWidgetConfig.settings.workplacesSource.hierarchy'),
            },
            {
              value: DataTableWidgetConfig.SOURCE_GROUP,
              label: i18n.t('dataTableWidgetConfig.settings.workplacesSource.group'),
            },
            {
              value: DataTableWidgetConfig.SOURCE_SELECTION,
              label: i18n.t('dataTableWidgetConfig.settings.workplacesSource.selection'),
            },
          ],
        },
      ];
    }
    return [];
  }

  settings = [
    ...this.getAdditionalSettings(),
    {
      id: 'workplaces',
      label: i18n.t('dataTableWidgetConfig.settings.workplaces.label'),
      helpText: i18n.t('dataTableWidgetConfig.settings.workplaces.help'),
      type: WidgetSettingFormField.fieldTypes.HierarchyTreeMultiple,
      validations: [ValidationRules.requiredIf(!this.getIsWorkplaceCockpit())],
    },
    {
      id: 'orientation',
      label: i18n.t('dataTableWidgetConfig.settings.orientation.label'),
      helpText: i18n.t('dataTableWidgetConfig.settings.orientation.help'),
      type: WidgetSettingFormField.fieldTypes.Select,
      defaultValue: 'vertical',
      validations: [ValidationRules.required],
      options: [
        {value: 'vertical', label: i18n.t('dataTableWidgetConfig.settings.orientation.vertical')},
        {value: 'horizontal', label: i18n.t('dataTableWidgetConfig.settings.orientation.horizontal')},
      ],
    },
    {
      id: 'showWorkplace',
      label: i18n.t('dataTableWidgetConfig.settings.showWorkplace.label'),
      helpText: i18n.t('dataTableWidgetConfig.settings.showWorkplace.help'),
      type: WidgetSettingFormField.fieldTypes.Boolean,
      defaultValue: true,
    },
    {
      id: 'linkWorkplaceTo',
      label: i18n.t('dataTableWidgetConfig.settings.linkWorkplaceTo.label'),
      helpText: i18n.t('dataTableWidgetConfig.settings.linkWorkplaceTo.help'),
      type: WidgetSettingFormField.fieldTypes.Select,
      defaultValue: 'cockpit',
      options: [
        {
          value: DataTableWidgetConfig.LINK_WORKPLACE_NOWHERE,
          label: i18n.t('dataTableWidgetConfig.settings.linkWorkplaceTo.none'),
        },
        {
          value: DataTableWidgetConfig.LINK_WORKPLACE_TO_COCKPIT,
          label: i18n.t('dataTableWidgetConfig.settings.linkWorkplaceTo.cockpit'),
        },
        {
          value: DataTableWidgetConfig.LINK_WORKPLACE_TO_PRODUCTION,
          label: i18n.t('dataTableWidgetConfig.settings.linkWorkplaceTo.production'),
        },
      ],
    },
    {
      id: 'linkWorkplaceInNewTab',
      label: i18n.t('dataTableWidgetConfig.settings.linkWorkplaceInNewTab.label'),
      helpText: i18n.t('dataTableWidgetConfig.settings.linkWorkplaceInNewTab.help'),
      type: WidgetSettingFormField.fieldTypes.Boolean,
      defaultValue: false,
    },
  ];

  dataSource = [
    {
      id: 'fields',
      type: WidgetSettingFormField.fieldTypes.ModelPropertySelectField,
      modelOptions: this.modelOptions,
    },
  ];

  getWorkplacesSource() {
    return this.getSettingValue('settings', 'workplacesSource') || DataTableWidgetConfig.SOURCE_SELECTION;
  }

  getShowWorkplace() {
    return this.getSettingValue('settings', 'showWorkplace');
  }

  setSelectedWorkplaces() {
    return this.getSettingValue('settings', 'workplaces') || [];
  }
}
